import {
  Close, Restore, Save, Warning,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DataGrid, GridEditInputCell, useGridApiContext } from '@mui/x-data-grid';
import * as React from 'react';
import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { BUTTON_COLORS, VARIANTS } from 'shared/constants';

function strictParseInt(str) {
  if (/^\d+$/.test(str)) { // This regex checks if the string contains only digits
    return Number.parseInt(str, 10);
  }
  return NaN;
}

/**
 * @param {Object} props
 * @param {number} props.id - Row ID
 * @param {number} [props.value=''] - VLAN value
 * @param {string} [props.field=''] - Field name
 * @param {boolean} props.hasFocus - Whether cell has focus
 * @param {number} props.minVlan - Minimum VLAN value
 * @param {number} props.maxVlan - Maximum VLAN value
 */
function VlanEditCell(props) {
  const {
    id, value = '', field = '', hasFocus, minVlan, maxVlan, ...rest
  } = props;
  const apiRef = useGridApiContext();
  const ref = useRef();

  React.useLayoutEffect(() => {
    if (hasFocus) {
      ref.current.focus();
    }
  }, [hasFocus]);

  useEffect(() => {
    ref.current.addEventListener('wheel', (e) => e.preventDefault(), { passive: false });
  }, []);

  const handleChange = (e) => {
    const newValue = e.target.value; // The new value entered by the user
    if (newValue === '') {
      apiRef.current.setEditCellValue({ id, field, value: 0 });
      return;
    }
    if (!strictParseInt(newValue)) return;
    apiRef.current.setEditCellValue({ id, field, value: newValue });
  };

  return (
    <GridEditInputCell
      {...rest}
      id={id}
      ref={ref}
      type="number"
      value={value}
      field="vlan"
      hasFocus={hasFocus}
      onChange={handleChange}
      inputProps={{
        max: maxVlan,
        min: minVlan,
      }}
    />
  );
}

/**
 * @param {Object} props
 * @param {Array} props.data - Array of unit data with VLANs
 * @param {number} props.minVlan - Minimum VLAN value
 * @param {number} props.maxVlan - Maximum VLAN value
 * @param {Function} props.onSave - Callback function when saving changes
 */
export default function VlanEditDataGrid({
  data, minVlan, maxVlan, onSave,
}) {
  const [rows, setRows] = useState(data);
  const [dirty, setDirty] = useState(false);
  const [invalid, setInvalid] = useState([]);
  const [invalidConfirmOpen, setInvalidConfirmOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const theme = useTheme();
  const compactedColumns = useMediaQuery(theme.breakpoints.down('sm'));
  const warningFullScreen = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (rows.length !== data.length) {
      setDirty(true);
    }
  }, [data, rows]);

  useEffect(() => {
    setRows(data);
  }, [data]);

  const handleRevert = useCallback(() => {
    setRows(() => {
      setDirty(false);
      return data;
    });
    setInvalid([]);
  }, [data]);

  const handleSave = () => {
    const invalidRows = rows.filter((cr) => cr.vlan < minVlan || cr.vlan > maxVlan);
    if (invalidRows.length > 0) {
      setInvalidConfirmOpen(true);
      setInvalid(invalidRows);
    } else {
      onSave(rows);
      setDirty(false);
    }
  };

  const handleRowUpdate = (newRow) => {
    // Check if the latest row update incurred a change.
    const currRow = data.find((r) => r.id === newRow.id);
    const currChanged = newRow.vlan !== currRow.vlan;
    if (currChanged) {
      setRows(rows.map((row) => (row.id === newRow.id ? newRow : row)));
    }

    // Check if the existing state has changes.
    const hasPrevChanges = rows.some((row) => {
      const curr = data.find((d) => d.id === row.id);
      return curr.vlan !== row.vlan;
    });

    setDirty(hasPrevChanges || currChanged);

    return newRow;
  };

  const columns = useMemo(() => [
    {
      field: 'name',
      headerName: 'Unit',
      type: 'string',
      width: compactedColumns ? 150 : 300,
    },
    {
      field: 'vlan',
      headerName: 'Vlan',
      type: 'number',
      width: compactedColumns ? 150 : 200,
      editable: true,
      renderEditCell: (params) => (
        <VlanEditCell
          id={params.id}
          field={params.field}
          minVlan={minVlan}
          maxVlan={maxVlan}
          value={params.value}
          hasFocus
          {...params}
        />
      ),
      renderCell: (params) => params.value,
    },
  ], [compactedColumns, maxVlan, minVlan]);

  return (
    <>
      <Box>
        <Box sx={{
          width: '100%',
        }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'end',
              mb: 2,
            }}
          >
            <Typography color="text.secondary">
              Total Results&nbsp;
              {data.length}
            </Typography>
          </Box>
          {data.length === 0 ? (
            <Typography>This building has no units</Typography>
          ) : (
            <DataGrid
              rows={rows}
              columns={columns}
              processRowUpdate={handleRowUpdate}
              experimentalFeatures={{ clipboardPaste: true }}
              onCellEditStart={() => setEditing(true)}
              onCellEditStop={() => setEditing(false)}
              pageSizeOptions={[100]}
              hideFooter={dirty || editing || rows.length <= 100}
            />
          )}
        </Box>
        <Box
          style={{ marginBottom: 8 }}
          sx={{
            display: 'flex',
            justifyContent: 'end',
            gap: 1,
            py: 2,
          }}
        >
          {dirty && (
          <Button
            onClick={handleRevert}
            startIcon={<Restore />}
          >
            Discard all changes
          </Button>
          )}
          <Button
            disabled={!dirty || editing}
            onClick={handleSave}
            startIcon={<Save />}
            variant={VARIANTS.contained}
          >
            Save
          </Button>
        </Box>
      </Box>
      <Dialog open={invalidConfirmOpen} maxWidth="sm" fullWidth fullScreen={warningFullScreen}>
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Warning color="warning" />
            Invalid VLANs found
          </Box>
          <IconButton
            onClick={() => {
              setInvalidConfirmOpen(false);
              setInvalid([]);
            }}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack sx={{
            gap: 1.5,
          }}
          >
            <Typography>
              Expected VLAN range:
              {' '}
              <span style={{ fontWeight: 600 }}>
                {minVlan}
                {' '}
                -
                {' '}
                {maxVlan}
              </span>
            </Typography>
            <Typography>
              If you want to assign the following VLANs, you must first
              edit the VLAN range for this building and try again.
            </Typography>
            <Box>
              <Typography sx={{
                mb: 1,
              }}
              >
                Invalid unit VLANs:
              </Typography>
              <Stack
                sx={{
                  border: 1,
                  borderRadius: 1,
                  borderColor: (theme.vars || theme).palette.borderControl,
                }}
              >
                {invalid.map((iv) => (
                  <Box
                    key={iv.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1,
                    }}
                  >
                    <Typography>{iv.name}</Typography>
                    <Box
                      sx={{
                        borderRadius: 1,
                        bgcolor: (theme.vars || theme).palette.warning.main,
                        color: (theme.vars || theme).palette.warning.contrastText,
                        p: 1,
                        width: 75,
                        textAlign: 'center',
                      }}
                    >
                      {iv.vlan}
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            color={BUTTON_COLORS.error}
            onClick={() => {
              setInvalidConfirmOpen(false);
              setInvalid([]);
            }}
          >
            Go back
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
